import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../App';
import { simulationAPI } from '../lib/api';
import { t } from '../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, GitCompareArrows, CalendarDays } from 'lucide-react';
import { BeforeAfterSlider } from '../components/simulation/BeforeAfterSlider';
import { ShareActions } from '../components/simulation/ShareActions';
import { comparisonFilename } from '../lib/beforeAfterImage';

const toSrc = (b64) => (b64 ? `data:image/jpeg;base64,${b64}` : null);
const shortDate = (value, language) => new Date(value).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });

const Column = ({ sim, language, mode, title }) => {
  const src = toSrc(mode === 'real' ? sim.before_image : sim.after_image);
  return (
    <div className="space-y-2 min-w-0" data-testid={`compare-column-${title}`}>
      <div className="flex items-center gap-2 text-xs sm:text-sm text-white truncate">
        <CalendarDays className="w-4 h-4 text-[var(--lux-gold)] shrink-0" /> <span className="truncate">{shortDate(sim.created_at, language)}</span>
      </div>
      <div className="rounded-xl overflow-hidden border border-white/10 bg-black aspect-square">
        {src ? <img src={src} alt={title} className="w-full h-full object-contain" /> : <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">-</div>}
      </div>
    </div>
  );
};

export default function ComparePage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const idA = params.get('a');
  const idB = params.get('b');
  const [sims, setSims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('real'); // 'real' | 'ai'

  useEffect(() => {
    if (!idA || !idB || idA === idB) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([simulationAPI.get(idA), simulationAPI.get(idB)])
      .then(([ra, rb]) => {
        if (cancelled) return;
        // oldest on the left so the slider reads chronologically
        const ordered = [ra.data, rb.data].sort((x, y) => new Date(x.created_at) - new Date(y.created_at));
        setSims(ordered);
      })
      .catch(() => { if (!cancelled) { setSims(null); toast.error(t('compareLoadError', language)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [idA, idB, language]);

  const daysApart = useMemo(() => {
    if (!sims) return 0;
    return Math.max(0, Math.round((new Date(sims[1].created_at) - new Date(sims[0].created_at)) / 86400000));
  }, [sims]);

  const [older, newer] = sims || [];
  const leftSrc = sims ? toSrc(mode === 'real' ? older.before_image : older.after_image) : null;
  const rightSrc = sims ? toSrc(mode === 'real' ? newer.before_image : newer.after_image) : null;
  const labels = sims ? [shortDate(older.created_at, language), shortDate(newer.created_at, language)] : null;
  const modeLabel = mode === 'real' ? t('compareRealPhotos', language) : t('compareSimulations', language);
  const subtitle = sims ? `${t('compareTitle', language)} \u00b7 ${modeLabel} \u00b7 ${daysApart} ${t('compareDaysApart', language)}` : null;

  const goBack = () => navigate('/history?tab=simulations');

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-5xl mx-auto" data-testid="compare-page">
      <div className="fade-in flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl text-white flex items-center gap-2">
            <GitCompareArrows className="w-6 h-6 sm:w-7 sm:h-7 text-[var(--lux-gold)] shrink-0" /> {t('compareTitle', language)}
          </h1>
          <p className="text-white/50 text-sm mt-1" data-testid="compare-subtitle">
            {t('compareSubtitle', language)}{sims ? ` \u00b7 ${daysApart} ${t('compareDaysApart', language)}` : ''}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={goBack} className="bg-[var(--lux-panel)] border-white/10 text-white hover:bg-[var(--lux-panel-2)]" data-testid="compare-back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('compareBack', language)}
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-[480px] w-full bg-white/5 rounded-xl" data-testid="compare-loading" />
      ) : !sims ? (
        <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="compare-error">
          <CardContent className="py-12 text-center space-y-4">
            <p className="text-white/40 text-sm">{t('compareLoadError', language)}</p>
            <Button onClick={goBack} variant="outline" className="bg-[var(--lux-panel)] border-white/10 text-white" data-testid="compare-error-back-button">{t('compareBack', language)}</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-lux-panel/90 border-white/[0.08] overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base sm:text-lg font-display text-white" data-testid="compare-range-title">{labels[0]} → {labels[1]}</CardTitle>
              <Tabs value={mode} onValueChange={setMode} className="w-full sm:w-auto">
                <TabsList className="bg-black/20 border border-white/[0.08] w-full sm:w-auto grid grid-cols-2 sm:flex">
                  <TabsTrigger value="real" data-testid="compare-mode-real" className="data-[state=active]:bg-[var(--lux-gold)] data-[state=active]:text-[var(--lux-ink)] text-white/60 text-xs">{t('compareRealPhotos', language)}</TabsTrigger>
                  <TabsTrigger value="ai" data-testid="compare-mode-ai" className="data-[state=active]:bg-[var(--lux-gold)] data-[state=active]:text-[var(--lux-ink)] text-white/60 text-xs">{t('compareSimulations', language)}</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent className="pb-6 space-y-6">
            {leftSrc && rightSrc ? (
              <BeforeAfterSlider beforeSrc={leftSrc} afterSrc={rightSrc} language={language} labels={labels} hint={t('compareHint', language)} />
            ) : (
              <p className="text-white/40 text-sm text-center py-8" data-testid="compare-mode-unavailable">{t('compareLoadError', language)}</p>
            )}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Column sim={older} language={language} mode={mode} title="older" />
              <Column sim={newer} language={language} mode={mode} title="newer" />
            </div>
            <ShareActions
              beforeSrc={leftSrc}
              afterSrc={rightSrc}
              language={language}
              labels={labels ? labels.map(l => l.toUpperCase()) : null}
              subtitle={subtitle}
              filename={comparisonFilename(language)}
              shareTitle={t('compareShareTitle', language)}
              shareText={t('compareShareText', language)}
              downloadLabel={t('compareDownload', language)}
              shareLabel={t('compareShare', language)}
              testIdPrefix="compare"
              className="mt-0"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
